import React, { FC, useState } from 'react';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';

import Button from '../../components/common/button';
import { ChevronUpDownIcon } from '../../components/icons/chevron-up-down';
import { EditPencilIcon } from '../../components/icons/edit-pencil';
import { QuestionsCircleIcon } from '../../components/icons/question-circle';
import { Love } from '../../components/icons/reactions/love';
import { Joy } from '../../components/icons/reactions/joy';
import { Anger } from '../../components/icons/reactions/anger';
import { Fear } from '../../components/icons/reactions/fear';
import { Sadness } from '../../components/icons/reactions/sadness';
import { WheelPanel } from '../../components/tools/life-balance-wheel/wheelPanel';
import { SandTherapyBoard } from '../../components/tools/sand-therapy/SandTherapyBoard';
import type { PlacedFigure } from '../../components/tools/sand-therapy/types';

import type { IconProps } from '../../types/icon.types';
import type { IJournalResult } from '../../store/types';
import { useGetCloudsImagePresignedQuery } from '../../services/clouds.api';

import type { Comments } from './types';
import { JournalToolsInfo } from './journal-tools-info';

type Props = {
  meetData?: IJournalResult | null;
  editMode: boolean;
  commentsData: Comments;
  setCommentsData: React.Dispatch<React.SetStateAction<Comments>>;
  onSave: () => void;
  onToggleEditMode: () => void;
  onOpenConclusionTips: () => void;
  makeUrl: (url: string) => string;
  consultationId?: number;
  isGroupTherapy?: boolean;
};

const emotionSmile: Record<string, FC<IconProps>> = {
  Любовь: Love,
  Радость: Joy,
  Гнев: Anger,
  Страх: Fear,
  Грусть: Sadness,
};

// Компонент для отображения картинок облаков с функцией увеличения
const CloudsImageList: FC<{
  clouds: IJournalResult['clouds'];
  editMode: boolean;
  commentsData: Comments;
  setCommentsData: React.Dispatch<React.SetStateAction<Comments>>;
  consultationId?: number;
  isGroupTherapy?: boolean;
}> = ({ clouds, editMode, commentsData, setCommentsData, consultationId, isGroupTherapy = false }) => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);

  // Компонент для полноэкранного режима
  const FullscreenImageModal: FC<{ image: string; consultationId?: number; onClose: () => void }> = ({
    image,
    consultationId,
    onClose,
  }) => {
    const { data: presignedData, isLoading } = useGetCloudsImagePresignedQuery(
      {
        consultationId: consultationId?.toString() || '',
        filename: image,
      },
      {
        skip: !consultationId || !image,
      },
    );

    const imageUrl = presignedData?.url;

    return (
      <div className="fixed inset-0 z-[1000] bg-black/80 flex items-center justify-center">
        <div className="flex flex-col w-full h-full">
          <div className="flex items-center justify-between p-2">
            <div className="flex-1 flex justify-start">
              <button
                type="button"
                onClick={onClose}
                className="flex items-center gap-1 bg-black/60 text-white text-xs md:text-sm px-2 py-1 rounded hover:bg-black/70"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 9H5V5h4v2H7v2h2v2zm6-2h-2V5h4v4h-2V7zm-6 6H7v-2H5v4h4v-2zm6 0v2h2v2h-4v-4h2z" />
                </svg>
                Уменьшить
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : imageUrl ? (
              <img src={imageUrl} alt="Облака" className="max-w-full max-h-full object-contain rounded-[10px]" />
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  // Компонент для отдельного изображения облака
  const CloudImageItem: FC<{ image: string; index: number; onFullscreen: () => void }> = ({
    image,
    index,
    onFullscreen,
  }) => {
    const { data: presignedData, isLoading } = useGetCloudsImagePresignedQuery(
      {
        consultationId: consultationId?.toString() || '',
        filename: image,
      },
      {
        skip: !consultationId || !image,
      },
    );

    const imageUrl = presignedData?.url;

    if (isLoading) {
      return (
        <div
          className="relative w-full max-w-[500px] rounded-[10px] overflow-hidden bg-gray-100 flex items-center justify-center"
          style={{ minHeight: '300px' }}
        >
          <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (!imageUrl) {
      return null;
    }

    return (
      <div className="relative w-full max-w-[500px] rounded-[10px] overflow-hidden">
        <img src={imageUrl} alt="Облака" className="w-full h-auto" />
        <button
          type="button"
          onClick={onFullscreen}
          className="absolute top-2 left-2 flex items-center gap-1 z-20 bg-black/60 text-white text-xs md:text-sm px-2 py-1 rounded hover:bg-black/70"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
          </svg>
          Увеличить
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-10">
        {clouds.map((el, idx) => (
          <div className="flex flex-col gap-10" key={idx}>
            {/* Изображение рисунка */}
            {el.image && <CloudImageItem image={el.image} index={idx} onFullscreen={() => setFullscreenIndex(idx)} />}
            {/* Время использования и номер картинки */}
            <div>
              <div className="flex gap-[15px] text-sm mb-[10px]">
                <div className="text-greyBase">Время использования:</div>
                <div className="font-medium text-blackBase">{dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}</div>
              </div>
              <div className="flex gap-[15px] text-sm mb-[10px]">
                <div className="text-greyBase">Номер картинки:</div>
                <div className="font-medium text-blackBase">{el.selected_image_index + 1}</div>
              </div>
              {/* Комментарии психолога */}
              <div className="font-medium text-blackBase mb-[5px]">Комментарии психолога</div>
              {!editMode && <div className="text-greyBase">{el.comment}</div>}
              {editMode && (
                <textarea
                  value={commentsData.clouds?.[idx] || ''}
                  onChange={(e) => {
                    const next = (commentsData.clouds || []).map((item, index) =>
                      idx === index ? e.target.value : item,
                    );
                    setCommentsData((prev) => ({ ...prev, clouds: next }));
                  }}
                  className="w-full min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px]"
                  placeholder={
                    isGroupTherapy
                      ? 'Введите текст, например, ассоциации встречи, эврики, телесные сигналы и т.п.'
                      : 'Введите текст, например, ассоциации клиента, эврики, телесные сигналы и т.п.'
                  }
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Полноэкранный режим */}
      {fullscreenIndex !== null && clouds[fullscreenIndex]?.image && (
        <FullscreenImageModal
          image={clouds[fullscreenIndex].image}
          consultationId={consultationId}
          onClose={() => setFullscreenIndex(null)}
        />
      )}
    </>
  );
};

export const JournalMainTab = ({
  meetData,
  editMode,
  commentsData,
  setCommentsData,
  onSave,
  onToggleEditMode,
  onOpenConclusionTips,
  makeUrl,
  consultationId,
  isGroupTherapy = false,
}: Props) => {
  const ph = (forClient: string, forGroup: string) => (isGroupTherapy ? forGroup : forClient);

  const markdownComponents = {
    p: ({ className = '', ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <p className={`m-0 ${className}`.trim()} {...props} />
    ),
    ul: ({ className = '', ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <ul className={`m-0 pl-5 list-disc ${className}`.trim()} {...props} />
    ),
    ol: ({ className = '', ...props }: React.HTMLAttributes<HTMLOListElement>) => (
      <ol className={`m-0 pl-5 list-decimal ${className}`.trim()} {...props} />
    ),
    li: ({ className = '', ...props }: React.HTMLAttributes<HTMLLIElement>) => (
      <li className={`m-0 ${className}`.trim()} {...props} />
    ),
  };
  const hasDpdgContent = Boolean(
    meetData?.dpdg && ((meetData.dpdg.comment && meetData.dpdg.comment.trim().length > 0) || meetData.dpdg.date_time),
  );
  const hasQuestionsContent = Boolean(meetData?.questions && meetData.questions.length > 0);
  const hasWheelContent = Boolean(
    meetData?.wheel && meetData.wheel.some((item) => item.comment?.trim() || item.values?.length),
  );
  const hasEmotionsContent = Boolean(
    meetData?.emotions && meetData.emotions.some((emotion) => emotion.comment?.trim() || emotion.emotions?.length),
  );
  const hasMacContent = Boolean(
    meetData?.mac_cards &&
      meetData.mac_cards.some((card) => card.comment?.trim() || (Array.isArray(card.cards) && card.cards.length > 0)),
  );
  const hasColorCodeContent = Boolean(
    meetData?.color_code &&
      meetData.color_code.some((item) => item.colors?.length > 0 || item.comment?.trim() || item.analysis),
  );
  const hasCloudsContent = Boolean(
    meetData?.clouds && meetData.clouds.some((item) => item.image || item.comment?.trim()),
  );
  const hasSandTherapyContent = Boolean(
    meetData?.sand_therapy &&
      meetData.sand_therapy.some((item) => item.placed_figures?.length > 0 || item.comment?.trim()),
  );
  const hasGroupMacContent = Boolean(
    meetData?.mac_cards_by_participant &&
      Object.values(meetData.mac_cards_by_participant).some((arr) => arr?.some((e) => e.cards?.length)),
  );
  const hasGroupEmotionsContent = Boolean(
    meetData?.emotions_by_participant &&
      Object.values(meetData.emotions_by_participant).some((arr) => arr?.some((e) => e.emotions?.length)),
  );

  /** Участники групповой терапии: объединённый список identity с сохранением порядка (сначала по group_participant_names) */
  const groupParticipantIds = (() => {
    const fromMac = Object.keys(meetData?.mac_cards_by_participant ?? {});
    const fromEmo = Object.keys(meetData?.emotions_by_participant ?? {});
    const names = meetData?.group_participant_names ?? {};
    const set = new Set<string>([...fromMac, ...fromEmo]);
    const ordered = Object.keys(names).filter((id) => set.has(id));
    const rest = Array.from(set).filter((id) => !names[id]);
    return [...ordered, ...rest];
  })();
  const hasGroupTherapyContent = hasGroupMacContent || hasGroupEmotionsContent;

  const getParticipantLabel = (identity: string, index: number) =>
    meetData?.group_participant_names?.[identity] ?? `Участник ${index + 1}`;

  /** Редактирование одного блока «Комментарии психолога» в групповой терапии (своя кнопка Редактировать/Сохранить у каждого) */
  type EditingGroupComment = { identity: string; tool: 'mac' | 'emotions'; index: number };
  const [editingGroupComment, setEditingGroupComment] = useState<EditingGroupComment | null>(null);

  const isEditingGroupBlock = (identity: string, tool: 'mac' | 'emotions', index: number) =>
    editingGroupComment?.identity === identity &&
    editingGroupComment?.tool === tool &&
    editingGroupComment?.index === index;

  const revertGroupComment = (identity: string, tool: 'mac' | 'emotions', index: number) => {
    if (tool === 'mac') {
      const orig = meetData?.mac_cards_by_participant?.[identity]?.[index]?.comment ?? '';
      setCommentsData((prev) => {
        const prevMap = { ...(prev.mac_cards_by_participant ?? {}) };
        const arr = [...(prevMap[identity] ?? [])];
        arr[index] = orig;
        prevMap[identity] = arr;
        return { ...prev, mac_cards_by_participant: prevMap };
      });
    } else {
      const orig = meetData?.emotions_by_participant?.[identity]?.[index]?.comment ?? '';
      setCommentsData((prev) => {
        const prevMap = { ...(prev.emotions_by_participant ?? {}) };
        const arr = [...(prevMap[identity] ?? [])];
        arr[index] = orig;
        prevMap[identity] = arr;
        return { ...prev, emotions_by_participant: prevMap };
      });
    }
    setEditingGroupComment(null);
  };

  const handleSaveGroupComment = () => {
    onSave();
    setEditingGroupComment(null);
  };

  /** Редактирование «Заключение по сессии» отдельно (для групповой терапии, когда общей кнопки нет) */
  const [editingConclusion, setEditingConclusion] = useState(false);
  const handleSaveConclusion = () => {
    onSave();
    setEditingConclusion(false);
  };
  const revertConclusion = () => {
    setCommentsData((prev) => ({ ...prev, result: meetData?.result ?? '' }));
    setEditingConclusion(false);
  };

  /** Редактирование «Вывод по сессии» по участнику в групповой терапии */
  const [editingSessionConclusionIdentity, setEditingSessionConclusionIdentity] = useState<string | null>(null);
  const handleSaveSessionConclusion = () => {
    onSave();
    setEditingSessionConclusionIdentity(null);
  };
  const revertSessionConclusion = (identity: string) => {
    const orig = meetData?.session_conclusion_by_participant?.[identity] ?? '';
    setCommentsData((prev) => ({
      ...prev,
      session_conclusion_by_participant: {
        ...(prev.session_conclusion_by_participant ?? {}),
        [identity]: orig,
      },
    }));
    setEditingSessionConclusionIdentity(null);
  };

  const infoMacCard =
    !meetData || !meetData.mac_cards || meetData.mac_cards.length === 0 ? null : (
      <div className="flex flex-col gap-10">
        {meetData.mac_cards.map((el, idx) => (
          <div className="flex flex-col gap-10" key={idx}>
            <div className="flex gap-[16px] flex-wrap">
              {el.cards.map((card, cardIdx) => (
                <div key={cardIdx} className="w-[120px] h-[160px] shrink-0 rounded-[10px] overflow-hidden">
                  <img src={makeUrl(card)} className="w-full h-full object-cover" alt="" />
                </div>
              ))}
            </div>
            <div>
              <div className="flex gap-[15px] text-sm mb-[10px]">
                <div className="text-greyBase">Время использования:</div>
                <div className="font-medium text-blackBase">{dayjs(el?.date_time).format('DD.MM.YYYY HH:mm')}</div>
              </div>
              <div className="font-medium text-blackBase mb-[5px]">Комментарии психолога</div>
              {!editMode && <div className="text-greyBase">{el.comment}</div>}
              {editMode && (
                <textarea
                  value={commentsData.mac_cards[idx]}
                  onChange={(e) => {
                    const next = commentsData.mac_cards.map((item, index) => (idx === index ? e.target.value : item));
                    setCommentsData((prev) => ({ ...prev, mac_cards: next }));
                  }}
                  className="w-full min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px]"
                  placeholder={ph(
                    'Введите текст, например, ассоциации клиента, эврики, телесные “сигналы” и т.п.',
                    'Введите текст, например, ассоциации встречи, эврики, телесные “сигналы” и т.п.',
                  )}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    );

  const infoEmotions =
    !meetData || !meetData.emotions || meetData.emotions.length === 0 ? null : (
      <>
        {meetData.emotions.map((el, idx) => (
          <div className="flex flex-col mb-[10px]" key={idx}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {el.emotions.map((emotion, emotionIdx) => {
                const SmileComponent = emotionSmile[emotion.category] || null;

                return (
                  <div className="flex flex-col md:flex-row gap-2" key={emotionIdx}>
                    <div className="w-[120px] h-[160px] shrink-0 rounded-[10px] overflow-hidden flex">
                      <div className="w-full h-full box-border flex flex-col items-center justify-center gap-[15px] bg-grey5 p-3 rounded-[10px] text-center">
                        <div>
                          {SmileComponent ? (
                            <SmileComponent needHover={false} className="w-[80px] h-[80px] !cursor-default" />
                          ) : null}
                        </div>
                        <p className="font-medium text-blackBase">{emotion.category}</p>
                      </div>
                    </div>
                    <div>
                      <div className="w-full flex flex-wrap flex-col md:flex-row md:items-center gap-[5px] text-blackBase font-medium mb-[10px]">
                        {emotion.items.map((item, itemIdx) => (
                          <div className="bg-goldBase w-max p-[5px_15px] rounded-[5px]" key={itemIdx}>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-[15px] text-sm my-[10px]">
              <div className="text-greyBase">Время использования:</div>
              <div className="font-medium text-blackBase">{dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}</div>
            </div>
            <div className="font-medium text-blackBase mb-[5px]">Комментарии психолога</div>
            {editMode && (
              <textarea
                value={commentsData.emotions[idx]}
                onChange={(e) => {
                  const next = commentsData.emotions.map((item, index) => (idx === index ? e.target.value : item));
                  setCommentsData((prev) => ({ ...prev, emotions: next }));
                }}
                className="w-full min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px]"
                placeholder={ph(
                  'Введите текст, например, как клиент прокомментировал свое состояние',
                  'Введите текст, например, как на встрече прокомментировали своё состояние',
                )}
              />
            )}
            {!editMode && <div className="text-greyBase">{el.comment}</div>}
          </div>
        ))}
      </>
    );

  /** Групповая терапия: разбивка по людям — раскрыть участника и видеть все его инструменты (МАК, эмоции) */
  const infoGroupByParticipant =
    !hasGroupTherapyContent || groupParticipantIds.length === 0 ? null : (
      <div className="flex flex-col gap-4">
        {groupParticipantIds.map((identity, participantIndex) => {
          const macEntries = meetData?.mac_cards_by_participant?.[identity];
          const emoEntries = meetData?.emotions_by_participant?.[identity];
          const hasMac = macEntries?.some((e) => e.cards?.length);
          const hasEmo = emoEntries?.some((e) => e.emotions?.length);
          if (!hasMac && !hasEmo) return null;

          return (
            <details key={identity} className="group overflow-hidden border-t border-b border-[#EAEBEA]">
              <summary className="list-none cursor-pointer px-4 py-4 pb-4 font-medium text-blackBase flex items-center justify-between gap-2">
                <span className="font-manrope text-[24px] font-bold">
                  {participantIndex + 1}. {getParticipantLabel(identity, participantIndex)}
                </span>
                <span className="shrink-0 transition-transform group-open:rotate-180" aria-hidden>
                  <ChevronUpDownIcon direction="down" width={24} height={24} stroke="#262633" />
                </span>
              </summary>
              <div className="p-4 flex flex-col gap-8 min-w-0">
                {hasMac && macEntries && (
                  <div className="flex flex-col gap-4">
                    <h3 className="font-medium text-blackBase text-base">МАК-карты</h3>
                    <div className="flex flex-col gap-10 min-w-0">
                      {macEntries.map((el, idx) => (
                        <div className="flex flex-col gap-10 min-w-0" key={idx}>
                          <div className="flex gap-[16px] flex-wrap">
                            {el.cards?.map((card, cardIdx) => (
                              <div
                                key={cardIdx}
                                className="w-[120px] h-[160px] shrink-0 rounded-[10px] overflow-hidden"
                              >
                                <img src={makeUrl(card)} className="w-full h-full object-cover" alt="" />
                              </div>
                            ))}
                          </div>
                          <div className="min-w-0">
                            <div className="flex gap-[15px] text-sm mb-[10px]">
                              <div className="text-greyBase">Время использования:</div>
                              <div className="font-medium text-blackBase">
                                {dayjs(el?.date_time).format('DD.MM.YYYY HH:mm')}
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-2 mb-[5px]">
                              <span className="font-medium text-blackBase">Комментарии психолога</span>
                              {!isEditingGroupBlock(identity, 'mac', idx) && (
                                <button
                                  type="button"
                                  onClick={() => setEditingGroupComment({ identity, tool: 'mac', index: idx })}
                                  className="text-base font-comfortaa text-[#1D1B20] font-bold hover:underline shrink-0 flex items-center gap-4 cursor-pointer"
                                >
                                  Редактировать
                                  <EditPencilIcon width={24} height={24} stroke="currentColor" className="shrink-0" />
                                </button>
                              )}
                            </div>
                            {!isEditingGroupBlock(identity, 'mac', idx) && (
                              <div className="text-greyBase">
                                {commentsData.mac_cards_by_participant?.[identity]?.[idx] ?? el.comment ?? ''}
                              </div>
                            )}
                            {isEditingGroupBlock(identity, 'mac', idx) && (
                              <>
                                <textarea
                                  value={commentsData.mac_cards_by_participant?.[identity]?.[idx] ?? ''}
                                  onChange={(e) => {
                                    setCommentsData((prev) => {
                                      const prevMap = { ...(prev.mac_cards_by_participant ?? {}) };
                                      const prevArr = [
                                        ...(prevMap[identity] ?? macEntries.map((en) => en.comment ?? '')),
                                      ];
                                      prevArr[idx] = e.target.value;
                                      prevMap[identity] = prevArr;
                                      return { ...prev, mac_cards_by_participant: prevMap };
                                    });
                                  }}
                                  className="w-full min-w-0 min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px] box-border"
                                  placeholder='Введите текст, например, ассоциации участника, эврики, телесные "сигналы" и т.п.'
                                />
                                <div className="flex gap-2 mt-2">
                                  <Button
                                    type="black"
                                    className="!p-[6px_20px] text-sm"
                                    onClick={handleSaveGroupComment}
                                  >
                                    Сохранить
                                  </Button>
                                  <Button
                                    type="bordered"
                                    className="!p-[6px_20px] text-sm"
                                    onClick={() => revertGroupComment(identity, 'mac', idx)}
                                  >
                                    Отмена
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {hasEmo && emoEntries && (
                  <div className="flex flex-col gap-4">
                    <h3 className="font-medium text-blackBase text-base">Эмоциональное состояние</h3>
                    <div className="flex flex-col gap-6 min-w-0">
                      {emoEntries.map((el, idx) => (
                        <div className="flex flex-col mb-[10px] min-w-0" key={idx}>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {el.emotions?.map((emotion, emotionIdx) => {
                              const SmileComponent = emotionSmile[emotion.category] || null;
                              return (
                                <div className="flex flex-col md:flex-row gap-2" key={emotionIdx}>
                                  <div className="w-[120px] h-[160px] shrink-0 rounded-[10px] overflow-hidden flex">
                                    <div className="w-full h-full box-border flex flex-col items-center justify-center gap-[15px] bg-grey5 p-3 rounded-[10px] text-center">
                                      <div>
                                        {SmileComponent ? (
                                          <SmileComponent
                                            needHover={false}
                                            className="w-[80px] h-[80px] !cursor-default"
                                          />
                                        ) : null}
                                      </div>
                                      <p className="font-medium text-blackBase">{emotion.category}</p>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="w-full flex flex-wrap flex-col md:flex-row md:items-center gap-[5px] text-blackBase font-medium mb-[10px]">
                                      {emotion.items?.map((item, itemIdx) => (
                                        <div className="bg-goldBase w-max p-[5px_15px] rounded-[5px]" key={itemIdx}>
                                          {item}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex gap-[15px] text-sm my-[10px]">
                            <div className="text-greyBase">Время использования:</div>
                            <div className="font-medium text-blackBase">
                              {dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-2 mb-[5px]">
                            <span className="font-medium text-blackBase">Комментарии психолога</span>
                            {!isEditingGroupBlock(identity, 'emotions', idx) && (
                              <button
                                type="button"
                                onClick={() => setEditingGroupComment({ identity, tool: 'emotions', index: idx })}
                                className="text-base cursor-pointer font-comfortaa text-[#1D1B20] font-bold hover:underline shrink-0 flex items-center gap-4"
                              >
                                Редактировать
                                <EditPencilIcon width={24} height={24} stroke="currentColor" className="shrink-0" />
                              </button>
                            )}
                          </div>
                          {!isEditingGroupBlock(identity, 'emotions', idx) && (
                            <div className="text-greyBase">
                              {commentsData.emotions_by_participant?.[identity]?.[idx] ?? el.comment ?? ''}
                            </div>
                          )}
                          {isEditingGroupBlock(identity, 'emotions', idx) && (
                            <>
                              <textarea
                                value={commentsData.emotions_by_participant?.[identity]?.[idx] ?? ''}
                                onChange={(e) => {
                                  setCommentsData((prev) => {
                                    const prevMap = { ...(prev.emotions_by_participant ?? {}) };
                                    const prevArr = [
                                      ...(prevMap[identity] ?? emoEntries.map((en) => en.comment ?? '')),
                                    ];
                                    prevArr[idx] = e.target.value;
                                    prevMap[identity] = prevArr;
                                    return { ...prev, emotions_by_participant: prevMap };
                                  });
                                }}
                                className="w-full min-w-0 min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px] box-border"
                                placeholder="Введите текст, например, как участник прокомментировал своё состояние"
                              />
                              <div className="flex gap-2 mt-2">
                                <Button type="black" className="!p-[6px_20px] text-sm" onClick={handleSaveGroupComment}>
                                  Сохранить
                                </Button>
                                <Button
                                  type="bordered"
                                  className="!p-[6px_20px] text-sm"
                                  onClick={() => revertGroupComment(identity, 'emotions', idx)}
                                >
                                  Отмена
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Вывод по сессии — внизу каждого участника */}
                <div className="flex flex-col gap-2 min-w-0 border-t border-[#EAEBEA] pt-6">
                  <div className="flex items-center justify-between gap-2 mb-[5px]">
                    <span className="font-medium text-blackBase">Вывод по сессии</span>
                    {editingSessionConclusionIdentity !== identity && (
                      <button
                        type="button"
                        onClick={() => setEditingSessionConclusionIdentity(identity)}
                        className="text-base font-comfortaa text-[#1D1B20] font-bold hover:underline shrink-0 flex items-center gap-4 cursor-pointer"
                      >
                        Редактировать
                        <EditPencilIcon width={24} height={24} stroke="currentColor" className="shrink-0" />
                      </button>
                    )}
                  </div>
                  {editingSessionConclusionIdentity !== identity && (
                    <div className="text-greyBase">
                      {commentsData.session_conclusion_by_participant?.[identity] ??
                        meetData?.session_conclusion_by_participant?.[identity] ??
                        ''}
                    </div>
                  )}
                  {editingSessionConclusionIdentity === identity && (
                    <>
                      <textarea
                        value={commentsData.session_conclusion_by_participant?.[identity] ?? ''}
                        onChange={(e) => {
                          setCommentsData((prev) => ({
                            ...prev,
                            session_conclusion_by_participant: {
                              ...(prev.session_conclusion_by_participant ?? {}),
                              [identity]: e.target.value,
                            },
                          }));
                        }}
                        className="w-full min-w-0 min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px] box-border"
                        placeholder="Введите вывод по сессии для этого участника"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button type="black" className="!p-[6px_20px] text-sm" onClick={handleSaveSessionConclusion}>
                          Сохранить
                        </Button>
                        <Button
                          type="bordered"
                          className="!p-[6px_20px] text-sm"
                          onClick={() => revertSessionConclusion(identity)}
                        >
                          Отмена
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </details>
          );
        })}
      </div>
    );

  const infoDpdg =
    !meetData || !meetData.dpdg || !hasDpdgContent ? null : (
      <>
        {meetData.dpdg?.date_time && (
          <div className="flex gap-[15px] text-sm mb-[10px]">
            <div className="text-greyBase">Время использования:</div>
            <div className="font-medium text-blackBase">
              {dayjs(meetData.dpdg?.date_time).format('DD.MM.YYYY HH:mm')}
            </div>
          </div>
        )}
        <div className="font-medium text-blackBase mb-[5px]">Комментарии психолога</div>
        {!editMode && <div className="text-greyBase mb-[20px]">{meetData.dpdg.comment}</div>}
        {editMode && (
          <textarea
            className="w-full min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px]"
            placeholder="Введите текст"
            value={commentsData.dpdg}
            onChange={(e) => setCommentsData((prev) => ({ ...prev, dpdg: e.target.value }))}
          />
        )}
      </>
    );

  const infoQuestions =
    !meetData || !meetData.questions || meetData.questions.length === 0 ? null : (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-[20px] gap-y-[10px]">
        {meetData.questions.map((el, idx) => (
          <div className="flex gap-[10px]" key={idx}>
            <span className="text-blackBase text-sm grow">{el.question}</span>
            <span className="text-greyBase04 text-sm">{dayjs(el?.date_time).format('HH:mm')}</span>
          </div>
        ))}
      </div>
    );

  const infoWheels =
    !meetData || !meetData.wheel || meetData.wheel.length === 0 ? null : (
      <>
        {meetData.wheel.map((el, idx) => (
          <div className="flex flex-col max-w-full mb-[10px]" key={idx}>
            <WheelPanel values={el.values} inJournal />

            <div className="flex gap-[15px] text-sm my-[10px]">
              <div className="text-greyBase">Время использования:</div>
              <div className="font-medium text-blackBase">{dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}</div>
            </div>
            <div className="font-medium text-blackBase mb-[5px]">Комментарии психолога</div>
            {editMode && (
              <textarea
                value={commentsData.wheels[idx]}
                onChange={(e) => {
                  const next = commentsData.wheels.map((item, index) => (idx === index ? e.target.value : item));
                  setCommentsData((prev) => ({ ...prev, wheels: next }));
                }}
                className="w-full min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px]"
                placeholder={ph(
                  'Введите текст, например, как клиент прокомментировал свое состояние',
                  'Введите текст, например, как на встрече прокомментировали своё состояние',
                )}
              />
            )}
            {!editMode && <div className="text-greyBase">{el.comment}</div>}
          </div>
        ))}
      </>
    );

  const [expandedColorCode, setExpandedColorCode] = useState<Record<number, boolean>>({});

  const infoColorCode =
    !meetData || !meetData.color_code || meetData.color_code.length === 0 ? null : (
      <div className="flex flex-col gap-10">
        {meetData.color_code.map((item, idx) => {
          const isExpanded = expandedColorCode[idx] || false;

          return (
            <div className="flex flex-col gap-10" key={idx}>
              {/* Выбранные цвета */}
              <div className="flex gap-[16px] flex-wrap">
                {item.colors.map((color, colorIdx) => (
                  <div
                    key={colorIdx}
                    className="w-[61px] h-[78px] shrink-0 rounded-[10px]"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              {/* Диагностическое заключение */}
              {item.analysis?.diagnostics && item.analysis.diagnostics.length > 0 && (
                <div>
                  <div className="font-medium text-blackBase mb-[5px]">Диагностическое заключение</div>
                  <div className="text-greyBase mb-[10px] space-y-2">
                    {item.analysis.diagnostics.map((diagnostic, diagIdx) => (
                      <div key={diagIdx}>
                        <ReactMarkdown components={markdownComponents}>{diagnostic}</ReactMarkdown>
                      </div>
                    ))}
                  </div>
                  {(item.analysis.techniques?.length > 0 || item.analysis.hintQuestions?.length > 0) && (
                    <button
                      type="button"
                      onClick={() => setExpandedColorCode((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                      className="text-greyBase text-sm"
                    >
                      {isExpanded ? 'Скрыть подробности' : 'Подробнее...'}
                    </button>
                  )}

                  {/* Раскрывающийся блок с techniques и hintQuestions */}
                  {isExpanded && item.analysis && (
                    <div className="flex flex-col gap-6 pl-4 border-l-2 border-grey6 mt-4">
                      {item.analysis.hintQuestions && item.analysis.hintQuestions.length > 0 && (
                        <div>
                          <div className="font-medium text-blackBase mb-[5px]">Вопросы-подсказки</div>
                          <ul className="flex flex-col gap-2 text-greyBase list-decimal list-inside">
                            {item.analysis.hintQuestions.map((question, qIdx) => (
                              <li key={qIdx} className="pl-2">
                                <ReactMarkdown components={markdownComponents}>{question}</ReactMarkdown>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {item.analysis.techniques && item.analysis.techniques.length > 0 && (
                        <div>
                          <div className="font-medium text-blackBase mb-[5px]">Техники</div>
                          <ul className="flex flex-col gap-2 text-greyBase list-decimal list-inside">
                            {item.analysis.techniques.map((technique, techIdx) => (
                              <li key={techIdx} className="pl-2">
                                <ReactMarkdown components={markdownComponents}>{technique}</ReactMarkdown>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Время использования и комментарии психолога */}
              <div>
                <div className="flex gap-[15px] text-sm mb-[10px]">
                  <div className="text-greyBase">Время использования:</div>
                  <div className="font-medium text-blackBase">{dayjs(item.date_time).format('DD.MM.YYYY HH:mm')}</div>
                </div>
                <div className="font-medium text-blackBase mb-[5px]">Комментарии психолога</div>
                {!editMode && <div className="text-greyBase">{item.comment}</div>}
                {editMode && (
                  <textarea
                    value={commentsData.color_code[idx] || ''}
                    onChange={(e) => {
                      const next = commentsData.color_code.map((item, index) =>
                        idx === index ? e.target.value : item,
                      );
                      setCommentsData((prev) => ({ ...prev, color_code: next }));
                    }}
                    className="w-full min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px]"
                    placeholder="Введите текст"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );

  const infoClouds =
    !meetData || !meetData.clouds || meetData.clouds.length === 0 ? null : (
      <CloudsImageList
        clouds={meetData.clouds}
        editMode={editMode}
        commentsData={commentsData}
        setCommentsData={setCommentsData}
        consultationId={consultationId}
        isGroupTherapy={isGroupTherapy}
      />
    );

  const [sandTherapyFullscreenIndex, setSandTherapyFullscreenIndex] = useState<number | null>(null);

  const SAND_BOARD_W = 1200;
  const SAND_BOARD_H = 600;
  const SAND_PREVIEW_SCALE = 400 / SAND_BOARD_W;
  const SAND_PREVIEW_W = Math.round(SAND_BOARD_W * SAND_PREVIEW_SCALE);
  const SAND_PREVIEW_H = Math.round(SAND_BOARD_H * SAND_PREVIEW_SCALE);

  const infoSandTherapy =
    !meetData || !meetData.sand_therapy || meetData.sand_therapy.length === 0 ? null : (
      <div className="flex flex-col gap-10">
        {meetData.sand_therapy.map((el, idx) => (
          <div className="flex flex-col gap-10" key={idx}>
            <div
              className="relative rounded-[10px] overflow-hidden"
              style={{ width: SAND_PREVIEW_W, height: SAND_PREVIEW_H }}
            >
              <div
                style={{
                  width: SAND_BOARD_W,
                  height: SAND_BOARD_H,
                  transform: `scale(${SAND_PREVIEW_SCALE})`,
                  transformOrigin: 'top left',
                  pointerEvents: 'none',
                }}
              >
                <SandTherapyBoard
                  selectedFigures={[]}
                  placedFigures={el.placed_figures as PlacedFigure[]}
                  onPlaceFigure={() => {}}
                  onMoveFigure={() => {}}
                  onRotateFigure={() => {}}
                  onResizeFigure={() => {}}
                  onRemoveFigure={() => {}}
                  sandColor={el.sand_color}
                  readonly
                />
              </div>
              <button
                type="button"
                onClick={() => setSandTherapyFullscreenIndex(idx)}
                className="absolute top-2 left-2 flex items-center gap-1 z-20 bg-black/60 text-white text-xs md:text-sm px-2 py-1 rounded hover:bg-black/70"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
                </svg>
                Увеличить
              </button>
            </div>
            <div>
              <div className="flex gap-[15px] text-sm mb-[10px]">
                <div className="text-greyBase">Время использования:</div>
                <div className="font-medium text-blackBase">{dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}</div>
              </div>
              <div className="flex gap-[15px] text-sm mb-[10px]">
                <div className="text-greyBase">Фигур расставлено:</div>
                <div className="font-medium text-blackBase">{el.placed_figures.length}</div>
              </div>
              <div className="font-medium text-blackBase mb-[5px]">Комментарии психолога</div>
              {!editMode && <div className="text-greyBase">{el.comment}</div>}
              {editMode && (
                <textarea
                  value={commentsData.sand_therapy?.[idx] || ''}
                  onChange={(e) => {
                    const next = [...(commentsData.sand_therapy || [])];
                    next[idx] = e.target.value;
                    setCommentsData((prev) => ({ ...prev, sand_therapy: next }));
                  }}
                  className="w-full min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px]"
                  placeholder="Введите текст, например, наблюдения по расстановке фигур, символика, гипотезы"
                />
              )}
            </div>
          </div>
        ))}
        {sandTherapyFullscreenIndex !== null && meetData.sand_therapy[sandTherapyFullscreenIndex] && (
          <div className="fixed inset-0 z-[1000] bg-black/80 flex flex-col">
            <div className="flex-shrink-0 p-2">
              <button
                type="button"
                onClick={() => setSandTherapyFullscreenIndex(null)}
                className="flex items-center gap-1 bg-black/60 text-white text-xs md:text-sm px-2 py-1 rounded hover:bg-black/70"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 9H5V5h4v2H7v2h2v2zm6-2h-2V5h4v4h-2V7zm-6 6H7v-2H5v4h4v-2zm6 0v2h2v2h-4v-4h2z" />
                </svg>
                Уменьшить
              </button>
            </div>
            <div className="flex-1 min-h-0 flex items-center justify-center overflow-auto p-4">
              <div style={{ width: SAND_BOARD_W, height: SAND_BOARD_H, flexShrink: 0 }}>
                <SandTherapyBoard
                  selectedFigures={[]}
                  placedFigures={meetData.sand_therapy[sandTherapyFullscreenIndex].placed_figures as PlacedFigure[]}
                  onPlaceFigure={() => {}}
                  onMoveFigure={() => {}}
                  onRotateFigure={() => {}}
                  onResizeFigure={() => {}}
                  onRemoveFigure={() => {}}
                  sandColor={meetData.sand_therapy[sandTherapyFullscreenIndex].sand_color}
                  readonly
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );

  const footer = (
    <>
      <div className="w-full">
        <h2 className="flex items-center justify-between gap-2 font-comfortaa font-semibold text-lg lg:text-xl text-blackBase mb-[10px]">
          <span className="flex items-center gap-[5px]">
            <span>Заключение по сессии</span>
            <button type="button" onClick={onOpenConclusionTips}>
              <QuestionsCircleIcon className="cursor-pointer" />
            </button>
          </span>
          {hasGroupTherapyContent && !editingConclusion && (
            <button
              type="button"
              onClick={() => setEditingConclusion(true)}
              className="text-base font-comfortaa cursor-pointer text-[#1D1B20] font-bold hover:underline shrink-0 flex items-center gap-4"
            >
              Редактировать
              <EditPencilIcon width={24} height={24} stroke="currentColor" className="shrink-0" />
            </button>
          )}
        </h2>

        {hasGroupTherapyContent ? (
          <>
            {!editingConclusion && <div className="text-greyBase break-all">{commentsData.result}</div>}
            {editingConclusion && (
              <>
                <textarea
                  value={commentsData.result}
                  onChange={(e) => setCommentsData((prev) => ({ ...prev, result: e.target.value }))}
                  className="w-full min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px]"
                  placeholder="Введите текст, например, тип встречи, запрос, рабочие гипотезы, техники, методы, интервенция, что «забрали» с встречи и т.п."
                />
                <div className="flex gap-2 mt-2">
                  <Button type="black" className="!p-[6px_20px] text-sm" onClick={handleSaveConclusion}>
                    Сохранить
                  </Button>
                  <Button type="bordered" className="!p-[6px_20px] text-sm" onClick={revertConclusion}>
                    Отмена
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {!editMode && <div className="text-greyBase break-all">{meetData?.result}</div>}
            {editMode && (
              <textarea
                value={commentsData.result}
                onChange={(e) => setCommentsData((prev) => ({ ...prev, result: e.target.value }))}
                className="w-full min-h-[120px] border border-greyBase rounded-[10px] p-[13px_20px]"
                placeholder="Введите текст, например, тип клиента, запрос, рабочие гипотезы, техники, методы, интервенция, что клиент “забрал” и т.п."
              />
            )}
          </>
        )}
      </div>
      {!hasGroupTherapyContent && editMode && (
        <div className="flex justify-end mt-[40px]">
          <Button type="black" className="w-full md:w-max !p-[6px_30px] text-sm md:!text-base" onClick={onSave}>
            Сохранить
          </Button>
        </div>
      )}
    </>
  );

  return (
    <>
      {!hasGroupTherapyContent && (
        <div className="flex justify-end mb-6">
          <Button
            type="black"
            className="w-full md:w-max !p-[6px_30px] text-sm md:!text-base"
            onClick={onToggleEditMode}
          >
            {editMode ? 'Отмена' : 'Редактировать'}
          </Button>
        </div>
      )}

      {hasMacContent && <JournalToolsInfo title={'Мак-карта'}>{infoMacCard}</JournalToolsInfo>}

      {hasGroupTherapyContent && (
        <JournalToolsInfo title={'Работа с инструментами по участникам'}>{infoGroupByParticipant}</JournalToolsInfo>
      )}

      {hasWheelContent && <JournalToolsInfo title={'Колесо баланса'}>{infoWheels}</JournalToolsInfo>}

      {hasEmotionsContent && <JournalToolsInfo title={'Эмоциональное состояние'}>{infoEmotions}</JournalToolsInfo>}

      {hasDpdgContent && (
        <JournalToolsInfo title={'Десенсибилизация и переработка движением глаз (ДПДГ)'}>{infoDpdg}</JournalToolsInfo>
      )}
      {hasQuestionsContent && <JournalToolsInfo title={'Используемые вопросы'}>{infoQuestions}</JournalToolsInfo>}

      {hasColorCodeContent && <JournalToolsInfo title={'Цветовой код'}>{infoColorCode}</JournalToolsInfo>}

      {hasCloudsContent && <JournalToolsInfo title={'Облака'}>{infoClouds}</JournalToolsInfo>}

      {hasSandTherapyContent && <JournalToolsInfo title={'Песочная терапия'}>{infoSandTherapy}</JournalToolsInfo>}

      {footer}
    </>
  );
};
